import React, { useEffect, useMemo, useState } from "react";
import {
  Mail,
  Phone,
  Calendar as CalendarIcon,
  ShoppingBag,
  ChevronDown,
  ChevronUp,
  Reply,
  Inbox,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import AdminLayout from "../components/AdminLayout";

const FILTERS = [
  { key: "all", label: "All" },
  { key: "new", label: "New" },
  { key: "replying", label: "Replying" },
  { key: "closed", label: "Closed" },
];

const STATUS_STYLES = {
  new: "bg-[var(--mn-accent)] text-white",
  replying: "bg-[var(--mn-ink)] text-[var(--mn-bg)]",
  closed: "bg-neutral-200 text-[var(--mn-ink-2)]",
};

export default function AdminEnquiries() {
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [openId, setOpenId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/concierge", { params: { limit: 500 } });
      setEnquiries(r.data || []);
    } catch (_) {
      setEnquiries([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const counts = useMemo(() => {
    const c = { all: enquiries.length, new: 0, replying: 0, closed: 0 };
    enquiries.forEach((e) => {
      const s = e.status || "new";
      if (c[s] !== undefined) c[s] += 1;
    });
    return c;
  }, [enquiries]);

  const filtered = useMemo(() => {
    if (filter === "all") return enquiries;
    return enquiries.filter((e) => (e.status || "new") === filter);
  }, [enquiries, filter]);

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/concierge/${id}`, { status });
      setEnquiries((prev) => prev.map((e) => (e.id === id ? { ...e, status } : e)));
      toast.success(
        status === "replying" ? "Marked as replying." : status === "closed" ? "Closed." : "Reopened."
      );
    } catch (err) {
      toast.error("Could not update.");
    }
  };

  return (
    <AdminLayout title="Style Advice.">
      <div data-testid="admin-enquiries-page">
        {/* Filter pill row */}
        <div className="flex flex-wrap gap-2 mb-10" data-testid="enquiries-filters">
          {FILTERS.map((f) => {
            const active = filter === f.key;
            return (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                className={`px-5 py-2.5 text-xs uppercase tracking-[0.22em] border transition-colors ${
                  active
                    ? "bg-[var(--mn-ink)] text-[var(--mn-bg)] border-[var(--mn-ink)]"
                    : "border-[var(--mn-border-strong)] text-[var(--mn-ink-2)] hover:border-[var(--mn-ink)] hover:text-[var(--mn-ink)]"
                }`}
                data-testid={`enquiries-filter-${f.key}`}
              >
                {f.label}
                <span className={`ml-2 font-mono-luxe text-[11px] ${active ? "text-[var(--mn-bg)]/70" : "text-[var(--mn-muted)]"}`}>
                  {counts[f.key] ?? 0}
                </span>
              </button>
            );
          })}
        </div>

        {loading ? (
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-24 bg-neutral-200/60 animate-pulse" />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="border border-dashed border-[var(--mn-border-strong)] py-32 text-center" data-testid="enquiries-empty">
            <Inbox size={28} strokeWidth={1.2} className="mx-auto text-[var(--mn-muted)]" />
            <p className="font-display text-3xl mt-6">
              {filter === "all" ? "No enquiries yet." : `No ${filter} enquiries.`}
            </p>
            <p className="text-sm text-[var(--mn-ink-2)] mt-3">
              Style Advice submissions from your storefront will appear here.
            </p>
          </div>
        ) : (
          <ul className="border-y border-[var(--mn-border)] divide-y divide-[var(--mn-border)]">
            {filtered.map((e) => {
              const status = e.status || "new";
              const isOpen = openId === e.id;
              return (
                <li key={e.id} data-testid={`enquiry-${e.id}`}>
                  <button
                    onClick={() => setOpenId(isOpen ? null : e.id)}
                    className="w-full text-left py-5 flex items-start justify-between gap-6 hover:bg-neutral-100 px-3 -mx-3 transition-colors"
                    data-testid={`enquiry-toggle-${e.id}`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-3 flex-wrap">
                        <span
                          className={`font-mono-luxe text-[10px] uppercase tracking-[0.22em] px-2 py-1 ${STATUS_STYLES[status]}`}
                        >
                          {status}
                        </span>
                        <p className="font-display text-xl tracking-tight">{e.full_name}</p>
                        {e.interested_piece && (
                          <span className="text-xs text-[var(--mn-ink-2)] hidden md:inline">
                            · {e.interested_piece}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-[var(--mn-ink-2)] mt-2 truncate max-w-[60ch]">
                        {e.message || e.email}
                      </p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-[10px] uppercase tracking-[0.22em] text-[var(--mn-muted)]">
                        {new Date(e.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                      </p>
                      {isOpen ? (
                        <ChevronUp size={16} strokeWidth={1.5} className="ml-auto mt-2 text-[var(--mn-ink-2)]" />
                      ) : (
                        <ChevronDown size={16} strokeWidth={1.5} className="ml-auto mt-2 text-[var(--mn-ink-2)]" />
                      )}
                    </div>
                  </button>

                  {isOpen && (
                    <div className="pb-8 px-3 grid grid-cols-1 md:grid-cols-3 gap-8" data-testid={`enquiry-detail-${e.id}`}>
                      <div className="md:col-span-2">
                        <p className="eyebrow mb-3">Message</p>
                        <p className="text-sm text-[var(--mn-ink)] leading-relaxed whitespace-pre-line">
                          {e.message || <span className="text-[var(--mn-muted)] italic">— no message —</span>}
                        </p>

                        <div className="mt-8 grid grid-cols-2 gap-4 text-sm">
                          <Detail icon={<Mail size={14} strokeWidth={1.4} />} label="Email" value={
                            <a href={`mailto:${e.email}`} className="hover:text-[var(--mn-accent)] underline underline-offset-4">{e.email}</a>
                          } />
                          {e.phone && <Detail icon={<Phone size={14} strokeWidth={1.4} />} label="Phone" value={e.phone} />}
                          {e.preferred_date && <Detail icon={<CalendarIcon size={14} strokeWidth={1.4} />} label="Preferred date" value={e.preferred_date} />}
                          {e.interested_piece && <Detail icon={<ShoppingBag size={14} strokeWidth={1.4} />} label="Bag of interest" value={e.interested_piece} />}
                        </div>
                      </div>

                      <div>
                        <p className="eyebrow mb-3">Actions</p>
                        <div className="space-y-3">
                          <a
                            href={buildMailto(e)}
                            className="btn-primary w-full justify-center"
                            data-testid={`enquiry-reply-${e.id}`}
                          >
                            <Reply size={14} strokeWidth={1.5} /> Reply by email
                          </a>
                          {status !== "replying" && status !== "closed" && (
                            <button
                              onClick={() => setStatus(e.id, "replying")}
                              className="btn-ghost w-full justify-center"
                              data-testid={`enquiry-mark-replying-${e.id}`}
                            >
                              Mark as replying
                            </button>
                          )}
                          {status !== "closed" && (
                            <button
                              onClick={() => setStatus(e.id, "closed")}
                              className="btn-ghost w-full justify-center"
                              data-testid={`enquiry-mark-closed-${e.id}`}
                            >
                              Close enquiry
                            </button>
                          )}
                          {status === "closed" && (
                            <button
                              onClick={() => setStatus(e.id, "new")}
                              className="btn-ghost w-full justify-center"
                              data-testid={`enquiry-reopen-${e.id}`}
                            >
                              Reopen
                            </button>
                          )}
                          <p className="text-[10px] uppercase tracking-[0.22em] text-[var(--mn-muted)] pt-3">
                            Received {new Date(e.created_at).toLocaleString()}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </AdminLayout>
  );
}

function Detail({ icon, label, value }) {
  return (
    <div>
      <span className="text-[10px] uppercase tracking-[0.22em] text-[var(--mn-muted)] inline-flex items-center gap-1.5 mb-1">
        {icon} {label}
      </span>
      <div className="text-sm text-[var(--mn-ink)]">{value}</div>
    </div>
  );
}

function buildMailto(e) {
  const subject = encodeURIComponent(
    `Re: your enquiry${e.interested_piece ? ` — ${e.interested_piece}` : ""}`
  );
  const body = encodeURIComponent(
    `Hi ${e.full_name?.split(" ")[0] || "there"},\n\nThank you for reaching out to Elegant Affairs.\n\n`
  );
  return `mailto:${e.email}?subject=${subject}&body=${body}`;
}
